'use client';

import { useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { createBrowserClient, SupabaseConfigError } from '@/lib/supabase';
import { fbTrack } from '@/lib/analytics';

export default function SignupPage() {
  const router = useRouter();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [businessName, setBusinessName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (password !== confirmPassword) {
      setError('As senhas não coincidem');
      setLoading(false);
      return;
    }

    try {
      const supabase = createBrowserClient();

      const { error: authError } = await supabase.auth.signUp({
        email: email.toLowerCase().trim(),
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/auth/callback`,
          data: {
            business_name: businessName.trim(),
          }
        }
      });

      if (authError) {
        if (authError.message.includes('already registered')) {
          setError('Este email já está cadastrado');
        } else {
          setError('Erro ao criar conta. Tente novamente.');
        }
        return;
      }

      setSuccess(true);
      fbTrack('CompleteRegistration', { currency: 'BRL', value: 0 });
    } catch (err) {
      console.error('Signup error:', err);
      if (err instanceof SupabaseConfigError) {
        setError('Serviço temporariamente indisponível. Tente novamente em alguns minutos.');
      } else {
        setError('Erro de conexão. Tente novamente.');
      }
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <main className="min-h-screen bg-gradient-to-b from-gray-900 via-gray-900 to-black flex items-center justify-center px-4 pt-20 pb-8 md:pt-8">
        <div className="w-full max-w-md">
          <div className="bg-green-500/20 border border-green-500/50 rounded-2xl p-6 md:p-8 text-center">
            <div className="text-3xl md:text-4xl mb-3 md:mb-4">📧</div>
            <h2 className="text-xl md:text-2xl font-bold text-white mb-1.5 md:mb-2">
              Verifique seu email!
            </h2>
            <p className="text-gray-300 mb-4 md:mb-6 text-sm md:text-base">
              Enviamos um link de confirmação para <strong className="break-all">{email}</strong>
            </p>
            <p className="text-gray-400 text-xs md:text-sm">
              Clique no link do email para ativar sua conta e acessar o painel.
            </p>
            <Link
              href="/login"
              className="inline-block mt-5 md:mt-6 text-cyan-400 hover:underline text-sm md:text-base"
            >
              Ir para o login
            </Link>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-gradient-to-b from-gray-900 via-gray-900 to-black flex items-center justify-center px-4 pt-20 pb-8 md:pt-8">
      <div className="w-full max-w-md">


        {/* Card */}
        <div className="bg-white/5 border border-white/10 rounded-2xl p-6 md:p-8">
          {/* Trial badge */}
          <div className="flex justify-center mb-4">
            <span className="px-4 py-1.5 bg-green-500/20 border border-green-500/30 rounded-full text-green-400 text-xs font-medium">
              🎁 7 dias grátis • Sem cartão de crédito
            </span>
          </div>

          <h1 className="text-xl md:text-2xl font-bold text-white text-center mb-1.5 md:mb-2">
            Criar sua conta grátis
          </h1>
          <p className="text-gray-400 text-center text-sm md:text-base mb-6 md:mb-8">
            Comece a automatizar seu atendimento em 5 minutos
          </p>

          <form onSubmit={handleSignup} className="space-y-4">
            <div>
              <label htmlFor="businessName" className="block text-sm text-gray-300 mb-1.5 md:mb-2">
                Nome do seu negócio
              </label>
              <input
                id="businessName"
                type="text"
                value={businessName}
                onChange={(e) => setBusinessName(e.target.value)}
                placeholder="Ex: Loja da Maria"
                required
                className="w-full px-4 py-2.5 md:py-3 bg-white/10 border border-white/20 rounded-xl text-white text-sm md:text-base placeholder-gray-500 focus:outline-none focus:border-cyan-500 transition"
              />
            </div>

            <div>
              <label htmlFor="email" className="block text-sm text-gray-300 mb-1.5 md:mb-2">
                Email
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="seu@email.com"
                required
                className="w-full px-4 py-2.5 md:py-3 bg-white/10 border border-white/20 rounded-xl text-white text-sm md:text-base placeholder-gray-500 focus:outline-none focus:border-cyan-500 transition"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm text-gray-300 mb-1.5 md:mb-2">
                Senha
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Mínimo 6 caracteres"
                required
                minLength={6}
                className="w-full px-4 py-2.5 md:py-3 bg-white/10 border border-white/20 rounded-xl text-white text-sm md:text-base placeholder-gray-500 focus:outline-none focus:border-cyan-500 transition"
              />
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm text-gray-300 mb-1.5 md:mb-2">
                Confirmar Senha
              </label>
              <input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Digite sua senha novamente"
                required
                minLength={6}
                className="w-full px-4 py-2.5 md:py-3 bg-white/10 border border-white/20 rounded-xl text-white text-sm md:text-base placeholder-gray-500 focus:outline-none focus:border-cyan-500 transition"
              />
            </div>

            {error && (
              <p className="text-red-400 text-sm text-center">{error}</p>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 mt-2 md:py-4 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-xl text-white font-bold text-base md:text-lg hover:opacity-90 transition disabled:opacity-50 shadow-lg shadow-cyan-500/25"
            >
              {loading ? 'Criando conta...' : 'Começar Meu Teste Grátis →'}
            </button>

            <div className="flex flex-wrap justify-center gap-4 text-gray-500 text-xs">
              <span className="flex items-center gap-1">
                <span className="text-green-400">✓</span> Cancele quando quiser
              </span>
              <span className="flex items-center gap-1">
                <span className="text-green-400">✓</span> Suporte via WhatsApp
              </span>
            </div>
          </form>

          <div className="mt-5 md:mt-6 text-center">
            <p className="text-gray-400 text-sm md:text-base">
              Já tem conta?{' '}
              <Link href="/login" className="text-cyan-400 hover:underline">
                Fazer login
              </Link>
            </p>
          </div>
        </div>

        {/* Back to home */}
        <p className="text-center mt-5 md:mt-6">
          <Link href="/" className="text-gray-500 hover:text-gray-300 transition text-sm md:text-base">
            ← Voltar para o início
          </Link>
        </p>
      </div>
    </main>
  );
}
